import { Check, X, Minus } from "lucide-react";

/**
 * Comparison table — Excel/papir vs generic competitor vs Cleany.
 * Helps prospects understand WHY Cleany is different, not just better.
 */

const rows = [
    { feature: "Live-overblik over alle medarbejdere på job", excel: false, comp: "partial", cleany: true },
    { feature: "Mobil-app med foto-dokumentation", excel: false, comp: false, cleany: true },
    { feature: "Nøgledatabase med GPS-tracking", excel: false, comp: false, cleany: true },
    { feature: "Direkte integration til e-conomic / Dinero", excel: "manual", comp: false, cleany: true },
    { feature: "Reklamations-workflow med statistik", excel: false, comp: "partial", cleany: true },
    { feature: "Auto-afregning af underleverandører", excel: false, comp: false, cleany: true },
    { feature: "Dansk support — på dansk, fra Danmark", excel: "—", comp: false, cleany: true },
    { feature: "Branche-specifik UX (rengøring)", excel: false, comp: false, cleany: true },
];

const Cell = ({ value, highlight = false }) => {
    if (value === true) {
        return (
            <div className={`flex items-center justify-center ${highlight ? "" : ""}`}>
                <span className={`h-7 w-7 rounded-full ${highlight ? "bg-emerald-500" : "bg-emerald-100"} flex items-center justify-center`}>
                    <Check className={`h-4 w-4 ${highlight ? "text-white" : "text-emerald-600"}`} strokeWidth={3} />
                </span>
            </div>
        );
    }
    if (value === false) {
        return (
            <div className="flex items-center justify-center">
                <span className="h-7 w-7 rounded-full bg-rose-50 flex items-center justify-center">
                    <X className="h-4 w-4 text-rose-400" strokeWidth={2.5} />
                </span>
            </div>
        );
    }
    if (value === "partial") {
        return (
            <div className="flex items-center justify-center">
                <span className="h-7 w-7 rounded-full bg-amber-50 flex items-center justify-center">
                    <Minus className="h-4 w-4 text-amber-500" strokeWidth={3} />
                </span>
            </div>
        );
    }
    return (
        <div className="flex items-center justify-center">
            <span className="text-[11px] font-semibold text-slate-500">{value}</span>
        </div>
    );
};

export default function Comparison() {
    return (
        <section className="py-24 md:py-32 bg-white" data-testid="comparison-section">
            <div className="max-w-5xl mx-auto px-6 lg:px-10">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                        Hvorfor Cleany?
                    </div>
                    <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
                        Excel og generiske CRM&apos;er <br className="hidden md:block" />
                        passer ikke til rengøringsbranchen.
                    </h2>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed">
                        Cleany er bygget specifikt til rengøringsfirmaers hverdag. Her er hvad I får — og hvad I ikke får andetsteds.
                    </p>
                </div>

                <div className="mt-14 rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 overflow-hidden">
                    {/* Header row */}
                    <div className="grid grid-cols-12 bg-slate-50 border-b border-slate-200">
                        <div className="col-span-6 px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            Funktionalitet
                        </div>
                        <div className="col-span-2 px-3 py-4 text-center">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Excel + papir</div>
                        </div>
                        <div className="col-span-2 px-3 py-4 text-center">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Generisk CRM</div>
                        </div>
                        <div className="col-span-2 px-3 py-4 text-center bg-sky-600 text-white">
                            <div className="text-[11px] font-bold uppercase tracking-wider">Cleany</div>
                        </div>
                    </div>

                    {/* Rows */}
                    {rows.map((row, i) => (
                        <div
                            key={row.feature}
                            className={`grid grid-cols-12 items-center ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} ${
                                i < rows.length - 1 ? "border-b border-slate-100" : ""
                            }`}
                            data-testid={`comparison-row-${i}`}
                        >
                            <div className="col-span-6 px-5 py-4 text-sm font-medium text-slate-800">
                                {row.feature}
                            </div>
                            <div className="col-span-2 px-3 py-4">
                                <Cell value={row.excel} />
                            </div>
                            <div className="col-span-2 px-3 py-4">
                                <Cell value={row.comp} />
                            </div>
                            <div className="col-span-2 px-3 py-4 bg-sky-50/40">
                                <Cell value={row.cleany} highlight />
                            </div>
                        </div>
                    ))}

                    {/* Price row */}
                    <div className="grid grid-cols-12 items-center bg-slate-900 text-white">
                        <div className="col-span-6 px-5 py-4 text-sm font-bold">
                            Pris pr. md. (25 medarbejdere)
                        </div>
                        <div className="col-span-2 px-3 py-4 text-center text-[12px] font-semibold text-slate-400">
                            mange timer
                        </div>
                        <div className="col-span-2 px-3 py-4 text-center text-[12px] font-semibold text-slate-400">
                            4-5.000 kr
                        </div>
                        <div className="col-span-2 px-3 py-4 text-center bg-sky-600">
                            <div className="font-display text-lg font-bold tabular-nums">fra 1.495 kr</div>
                            <div className="text-[10px] text-sky-200/80">pr. måned</div>
                        </div>
                    </div>
                </div>

                <p className="mt-6 text-center text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5 mr-3">
                        <span className="h-3 w-3 rounded-full bg-emerald-100 inline-flex items-center justify-center"><Check className="h-2 w-2 text-emerald-600" strokeWidth={3} /></span>
                        Fuld
                    </span>
                    <span className="inline-flex items-center gap-1.5 mr-3">
                        <span className="h-3 w-3 rounded-full bg-amber-50 inline-flex items-center justify-center"><Minus className="h-2 w-2 text-amber-500" strokeWidth={3} /></span>
                        Delvis
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-rose-50 inline-flex items-center justify-center"><X className="h-2 w-2 text-rose-400" strokeWidth={3} /></span>
                        Ikke understøttet
                    </span>
                </p>
            </div>
        </section>
    );
}
